import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, DoorOpen, Layers, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ChartCard } from '@/components/shared/chart-card';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { StatCard } from '@/components/shared/stat-card';
import { VenueSelect } from '@/components/shared/venue-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { formatNumber } from '@/utils/format';

import {
  audiencePayload,
  emptyAudience,
  GroupAudienceFields,
  type GroupAudienceValue,
} from './group-audience-fields';

interface GroupRow {
  id: string;
  name: string;
  isOpen: boolean;
  isActive: boolean;
  memberCount: number;
  pendingJoinRequestCount: number;
}

interface ChurchGroupInsights {
  groupCount: number;
  membershipCount: number;
  openGroupCount: number;
  pendingJoinRequestCount: number;
  largest: Array<{ key: string; label: string; value: number }>;
}

export const CommitteesPage = () => {
  const { t } = useTranslation('committees');
  const { t: tGroups } = useTranslation('groups');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const membership = useChurchStore((state) => state.membership);
  const canCreate = hasPermission(membership, PERMISSIONS.GROUPS_CREATE);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [meetingVenueId, setMeetingVenueId] = useState<string | null>(null);
  const [audience, setAudience] = useState<GroupAudienceValue>(emptyAudience);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const list = useQuery({
    queryKey: ['committees', page, pageSize],
    queryFn: () =>
      api.get<{
        data: GroupRow[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(`/groups?type=manual&category=committee&page=${page}&pageSize=${pageSize}`),
  });

  const insights = useQuery({
    queryKey: ['committees', 'insights'],
    queryFn: () =>
      api.get<ChurchGroupInsights>('/groups/insights?type=manual&category=committee'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<GroupRow>('/groups', {
        name,
        type: 'manual',
        category: 'committee',
        isOpen,
        isActive,
        meetingVenueId,
        ...audiencePayload(audience),
      }),
    onSuccess: () => {
      toast.success(t('saved'));
      setOpen(false);
      setName('');
      setIsOpen(false);
      setIsActive(true);
      setMeetingVenueId(null);
      setAudience(emptyAudience());
      void queryClient.invalidateQueries({ queryKey: ['committees'] });
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<GroupRow>[] = [
    {
      id: 'name',
      header: tGroups('columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'members',
      header: tGroups('columns.members'),
      cell: (row) => tGroups('members', { count: row.memberCount }),
    },
    {
      id: 'pendingJoinRequests',
      header: tGroups('columns.pendingJoinRequests'),
      cell: (row) =>
        row.pendingJoinRequestCount > 0
          ? tGroups('pendingJoinRequests', { count: row.pendingJoinRequestCount })
          : '—',
    },
    {
      id: 'status',
      header: tGroups('columns.status'),
      cell: (row) => (
        <Badge variant={row.isActive ? 'success' : 'neutral'}>
          {row.isActive ? tGroups('statusActive') : tGroups('statusInactive')}
        </Badge>
      ),
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        tipId="groups.term"
        headerActions={
          canCreate ? <Button onClick={() => setOpen(true)}>{t('create')}</Button> : undefined
        }
      />

      <CollapsibleInsights
        summary={[
          {
            label: t('insights.committees'),
            value: insights.data ? formatNumber(insights.data.groupCount) : '…',
          },
          {
            label: t('insights.memberships'),
            value: insights.data ? formatNumber(insights.data.membershipCount) : '…',
          },
          {
            label: t('insights.openGroups'),
            value: insights.data ? formatNumber(insights.data.openGroupCount) : '…',
          },
          {
            label: t('insights.pendingJoinRequests'),
            value: insights.data ? formatNumber(insights.data.pendingJoinRequestCount) : '…',
          },
        ]}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label={t('insights.committees')}
            value={insights.data ? formatNumber(insights.data.groupCount) : undefined}
            icon={<Layers className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('insights.memberships')}
            value={insights.data ? formatNumber(insights.data.membershipCount) : undefined}
            icon={<Users className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('insights.openGroups')}
            value={insights.data ? formatNumber(insights.data.openGroupCount) : undefined}
            icon={<DoorOpen className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('insights.pendingJoinRequests')}
            value={
              insights.data ? formatNumber(insights.data.pendingJoinRequestCount) : undefined
            }
            icon={<ClipboardList className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

        <ChartCard
          title={t('insights.largest')}
          loading={insights.isLoading}
          hasData={(insights.data?.largest.length ?? 0) > 0}
          empty={{ title: t('insights.largestEmpty') }}
          height={160}
        >
          <SimpleBarChart
            orientation="horizontal"
            data={(insights.data?.largest ?? []).map((row) => ({
              label: row.label,
              value: row.value,
            }))}
            formatValue={(value) => formatNumber(value)}
            height={140}
          />
        </ChartCard>
      </CollapsibleInsights>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('empty')}</p>}
        onRowClick={(row) => void navigate(`/committees/${row.id}`)}
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <Pagination
          className="mt-4"
          page={pageMeta.page}
          pageSize={pageMeta.pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) =>
              tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('create')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label>{t('name')}</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{tGroups('meetingVenue')}</Label>
              <VenueSelect
                value={meetingVenueId}
                onChange={setMeetingVenueId}
                popoverPortal="dialog"
              />
            </div>
            <SwitchField
              id="committee-open"
              label={tGroups('open')}
              checked={isOpen}
              onCheckedChange={setIsOpen}
            />
            <SwitchField
              id="committee-active"
              label={tGroups('active')}
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <GroupAudienceFields value={audience} onChange={setAudience} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={create.isPending}>
                {t('save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
